import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Check, Copy, CircleAlert, CircleQuestionMark, Volume2 } from 'lucide-react';
import type { DictSource, DictionaryLookupResult, WordStatus } from '@en3/shared';
import { dictionaryApi } from '@/api/dictionary.api';
import { wordbookApi } from '@/api/wordbook.api';
import { track } from '@/api/events.api';
import { WORDBOOK_RELATED_KEYS, queryKeys } from '@/api/client';
import { toDisplayMessage } from '@/api/errors';
import { useIsAuthenticated } from '@/api/hooks/useAuth';
import { ROUTES } from '@/app/routes';
import { Badge } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { ErrorBlock, LoadingBlock } from '@/components/common/States';
import { cn } from '@/lib/cn';

/**
 * 点词查义卡片 —— 阅读器里最高频的交互。
 *
 * 几个刻意的决定：
 * 1. **缓存键带上下文**（`queryKeys.lookup(lemma, sentenceText)`）。同一个词在不同句子里的
 *    「文中义」可能不同，只用 lemma 做键会让用户在第二句点同一个词时看到上一句的释义。
 *    键由 `api/client` 统一定义，这里不手写字符串。
 * 2. **内容整体挂 `key` 重挂载**，而不是用 effect 重置状态。切换到另一个词时，
 *    "已加入生词本/已复制"这些瞬时状态必须跟着换词一起清掉；用 effect 重置会先闪一帧旧状态。
 * 3. **发音三级降级**：`audioUsUrl/audioUkUrl` → `speechSynthesis` → 提示"暂无发音"。
 *    词典音频是外部资源，缺是常态，不能让一个播放按钮变成死按钮。
 * 4. 加入生词本失败**不弹全局错误**，只在卡片内说明 —— 查词是阅读流的一部分，
 *    一次写库失败不该打断阅读。
 */

export interface WordCardTarget {
  /** 用户点的那串字符（原样，保留大小写） */
  surface: string;
  /** 词形还原结果，只用于缓存键与展示 */
  lemma: string;
  /** 文中义上下文（所在句子原文） */
  sentenceText: string;
  /** 句子 id（从正文点进来时有；从解析面板点进来时是解析的那句） */
  sentenceId?: string | undefined;
  /** 句内词序，与 `article_word_senses.token_index` 对齐 */
  tokenIndex?: number | undefined;
}

export interface WordCardProps {
  /** null 表示关闭 */
  target: WordCardTarget | null;
  articleId: string;
  /** 「点词自动加入生词本」开关（默认关；开启后按 fuzzy 自动收录） */
  autoCollect: boolean;
  onClose: () => void;
}

const SOURCE_LABELS: Record<DictSource, string> = {
  builtin: '平台内置词表',
  'free-dict': '在线开放词典',
  llm: 'AI 补全释义',
  manual: '人工录入',
  cache: '词典缓存',
  none: '暂无来源',
};

export function WordCard({ target, articleId, autoCollect, onClose }: WordCardProps) {
  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      title={target ? `查词 · ${target.surface}` : '查词'}
      variant="sheet"
      className="sm:max-w-xl"
    >
      {target && (
        // key 里带上句子与词序：换一个词就是一次全新的查词，状态不该残留
        <WordCardBody
          key={`${target.lemma}@${target.sentenceId ?? '-'}#${target.tokenIndex ?? -1}`}
          target={target}
          articleId={articleId}
          autoCollect={autoCollect}
        />
      )}
    </Dialog>
  );
}

function WordCardBody({
  target,
  articleId,
  autoCollect,
}: {
  target: WordCardTarget;
  articleId: string;
  autoCollect: boolean;
}) {
  const authenticated = useIsAuthenticated();
  const queryClient = useQueryClient();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [joined, setJoined] = useState<WordStatus | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  /** 没有音频文件且浏览器不支持朗读时才有值 —— 用于把播放按钮换成不可用提示 */
  const [speechUnavailable, setSpeechUnavailable] = useState(false);

  const query = useQuery({
    queryKey: queryKeys.lookup(target.lemma, target.sentenceText),
    queryFn: async (): Promise<DictionaryLookupResult> =>
      dictionaryApi.lookup({
        word: target.surface,
        context: target.sentenceText,
        articleId,
        sentenceId: target.sentenceId,
        tokenIndex: target.tokenIndex,
        // track 让后端累加该词的查词次数（水平评估的特征之一）
        track: true,
      }),
    enabled: true,
  });

  const data = query.data;
  const entry = data?.entry ?? null;
  const sense = data?.senseInContext ?? null;
  const audioUrl = entry?.audioUsUrl ?? entry?.audioUkUrl ?? null;
  // 把"有没有徽章"算成布尔值：直接用 `a || b` 做 `&&` 条件时，数值 0 会被 React 渲染成 "0"
  const hasBadges = Boolean(entry && (entry.pos.length > 0 || entry.cefrLevel || entry.cocaRank || entry.zipf));

  const createRecord = useMutation({
    mutationFn: (status: WordStatus) =>
      wordbookApi.create({
        wordId: entry?.lemma ?? data?.matched ?? undefined,
        surface: target.surface,
        status,
        articleId,
        // 记下"第一次遇到它时的那句话"，复习页的挖空句就靠它
        contextSnippet: target.sentenceText,
        countLookup: true,
      }),
    onSuccess: (_result, status) => {
      setJoined(status);
      setNotice('已加入生词本，可在「生词本」里复习或改状态。');
      // 生词本是多个页面的数据源（首页统计、个人中心、复习队列），写后统一失效
      for (const key of WORDBOOK_RELATED_KEYS) void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (error) => setNotice(toDisplayMessage(error)),
  });

  /**
   * 查词成功后补一条埋点。
   *
   * 用 `data` 的引用同时做两件事：① 同一个查询结果只上报一次（StrictMode 下 effect 会跑两遍）；
   * ② 重新查询（换句/换词）才算一条新事件。
   */
  const trackedLookup = useRef<DictionaryLookupResult | null>(null);
  useEffect(() => {
    if (!data || trackedLookup.current === data) return;
    trackedLookup.current = data;
    track({
      type: 'lookup',
      articleId: articleId || undefined,
      wordId: data.matched ?? undefined,
      payload: { word: data.query },
    });
  }, [data, articleId]);

  /** 自动收录：只对"查到了词条"的结果做，且每次查询结果只做一次。 */
  const autoCollected = useRef<DictionaryLookupResult | null>(null);
  // 用 `mutate` 而不是 `mutateAsync`：v5 里 `mutate` 由 useCallback([observer]) 产出、引用恒定，
  // 可以安全地进 effect 依赖；`mutateAsync` 来自 store 快照，引用会随 mutation 状态变化。
  const collectWord = createRecord.mutate;
  useEffect(() => {
    if (!autoCollect || !authenticated || !data || !entry) return;
    if (autoCollected.current === data) return;
    autoCollected.current = data;
    // 自动收录是"顺手做的事"，`mutate` 内部已吞掉异常，不打扰用户
    collectWord('fuzzy');
  }, [autoCollect, authenticated, data, entry, collectWord]);

  function speak(text: string) {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      setSpeechUnavailable(true);
      return;
    }
    try {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    } catch {
      setSpeechUnavailable(true);
    }
  }

  function playWord() {
    const audio = audioRef.current;
    if (audioUrl && audio) {
      audio.currentTime = 0;
      void audio.play().catch(() => speak(target.surface));
      return;
    }
    speak(target.surface);
  }

  async function copySentence() {
    try {
      await navigator.clipboard.writeText(target.sentenceText);
      setCopied(true);
    } catch {
      // 剪贴板在 http 非安全上下文/无权限时会抛错：静默，用户从提示缺失即知失败
    }
  }

  const joinedLabel = joined === 'unknown' ? '不认识' : joined === 'fuzzy' ? '模糊' : '认识';

  return (
    <div className="space-y-4 text-sm">
      {/* 词头：拼写 + 音标 + 发音 */}
      <section className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-serif text-2xl leading-tight break-all">{entry?.display ?? target.surface}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {entry?.phoneticUk && <span>英 /{entry.phoneticUk}/</span>}
            {entry?.phoneticUs && <span>美 /{entry.phoneticUs}/</span>}
            {entry && !entry.phoneticUk && !entry.phoneticUs && <span>暂无音标</span>}
          </div>
          {/* 点 studies 出来的是 study 时，要说明"为什么" */}
          {data?.matched && data.matched.toLowerCase() !== target.surface.toLowerCase() && (
            <p className="mt-1 text-xs text-muted-foreground">
              「{target.surface}」按原形「{data.matched}」收录
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1">
          {audioUrl && <audio ref={audioRef} src={audioUrl} preload="none" className="hidden" />}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={playWord}
            disabled={speechUnavailable}
            title={audioUrl ? '播放词典发音' : '用系统朗读读这个词'}
          >
            <Volume2 aria-hidden className="size-4" />
            {speechUnavailable ? '暂无发音' : '发音'}
          </Button>
          {!audioUrl && !speechUnavailable && (
            <span className="text-[10px] text-muted-foreground">无音频，用系统朗读</span>
          )}
        </div>
      </section>

      {/* 徽章行 */}
      {hasBadges && (
        <div className="flex flex-wrap items-center gap-1.5">
          {entry?.cefrLevel && <Badge tone="primary">CEFR {entry.cefrLevel}</Badge>}
          {entry?.pos.map((pos) => (
            <Badge key={pos} tone="muted">
              {pos}
            </Badge>
          ))}
          {typeof entry?.cocaRank === 'number' && <Badge tone="muted">词频 第 {entry.cocaRank} 位</Badge>}
          {typeof entry?.zipf === 'number' && <Badge tone="muted">zipf {entry.zipf.toFixed(2)}</Badge>}
        </div>
      )}

      {query.isPending && <LoadingBlock label="正在查询词典…" />}

      {query.isError && (
        <ErrorBlock message={toDisplayMessage(query.error)} onRetry={() => void query.refetch()} />
      )}

      {/* 已经查到词条才显示正文；未收录时给"可先加入生词本"的出口 */}
      {data && !entry && (
        <p className="rounded-md border border-dashed px-3 py-3 text-muted-foreground">
          词典暂未收录该词，可先加入生词本，之后在生词本里补笔记。
        </p>
      )}

      {entry && (
        <>
          {/* 文中义：这张卡片最有价值的一块，单独高亮，且必须标注"在本句中" */}
          {sense && (sense.senseZh || sense.senseEn) && (
            <section className="rounded-md border-l-2 border-primary bg-accent/50 px-3 py-2">
              <p className="text-xs font-semibold text-primary">
                在本句中的意思
                {sense.pos && <span className="ml-1 font-normal">{sense.pos}</span>}
              </p>
              {sense.senseZh && <p className="mt-1">{sense.senseZh}</p>}
              {sense.senseEn && <p className="mt-0.5 text-xs text-muted-foreground">{sense.senseEn}</p>}
              {typeof sense.confidence === 'number' && (
                <p className="mt-1 text-[10px] text-muted-foreground">
                  把握度 {Math.round(sense.confidence * 100)}%
                  {sense.confidence < 0.5 && '（规则消歧结果，仅供参考）'}
                </p>
              )}
            </section>
          )}

          {entry.defsZh.length > 0 && (
            <section>
              <SectionTitle>中文释义</SectionTitle>
              <ol className="mt-1 space-y-1">
                {entry.defsZh.map((def, index) => (
                  <li key={`${def}-${index}`} className="flex gap-2">
                    <span className="text-muted-foreground">{index + 1}.</span>
                    <span>{def}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {entry.defsEn.length > 0 && (
            <section>
              <SectionTitle>英文释义</SectionTitle>
              <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                {entry.defsEn.map((def, index) => (
                  <li key={`${def}-${index}`}>{def}</li>
                ))}
              </ul>
            </section>
          )}

          {entry.examples.length > 0 && (
            <section>
              <SectionTitle>例句</SectionTitle>
              <ul className="mt-1 space-y-2">
                {entry.examples.map((example, index) => (
                  <li key={`${example.en}-${index}`} className="rounded-md border px-3 py-2">
                    <p>{example.en}</p>
                    {example.zh && <p className="mt-0.5 text-xs text-muted-foreground">{example.zh}</p>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {Object.keys(entry.exchange).length > 0 && (
            <section>
              <SectionTitle>词形变化</SectionTitle>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {Object.entries(entry.exchange).map(([label, form]) => (
                  <Badge key={label} tone="muted">
                    {label} {form}
                  </Badge>
                ))}
              </div>
            </section>
          )}

          <p className="text-[11px] text-muted-foreground">
            来源：{SOURCE_LABELS[entry.source]}
            {data?.cached && ' · 本次命中缓存'}
          </p>
        </>
      )}

      {/* 降级说明必须显式展示：用户要知道自己看的是"内置词表兜底"而不是完整词典 */}
      {data?.degraded && data.notes.length > 0 && (
        <p className="flex gap-2 rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
          <CircleAlert aria-hidden className="mt-0.5 size-3.5 shrink-0" />
          <span>{data.notes.join('；')}</span>
        </p>
      )}

      {target.sentenceText && (
        <section className="rounded-md bg-muted/50 px-3 py-2">
          <SectionTitle>本句</SectionTitle>
          <p className="mt-1 font-serif">{target.sentenceText}</p>
        </section>
      )}

      {/* 动作区：认识度标记 + 朗读整句 + 复制 */}
      <section className="space-y-2 border-t pt-3">
        {authenticated ? (
          <>
            <p className="text-xs text-muted-foreground">这个词你认识吗？（决定它怎么进入复习队列）</p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={joined === 'unknown' ? 'primary' : 'outline'}
                size="sm"
                loading={createRecord.isPending && createRecord.variables === 'unknown'}
                onClick={() => createRecord.mutate('unknown')}
              >
                <Ban aria-hidden className="size-3.5" />
                不认识
              </Button>
              <Button
                type="button"
                variant={joined === 'fuzzy' ? 'primary' : 'outline'}
                size="sm"
                loading={createRecord.isPending && createRecord.variables === 'fuzzy'}
                onClick={() => createRecord.mutate('fuzzy')}
              >
                <CircleQuestionMark aria-hidden className="size-3.5" />
                模糊
              </Button>
              <Button
                type="button"
                variant={joined === 'known' ? 'primary' : 'outline'}
                size="sm"
                loading={createRecord.isPending && createRecord.variables === 'known'}
                onClick={() => createRecord.mutate('known')}
              >
                <Check aria-hidden className="size-3.5" />
                认识
              </Button>
            </div>
            {joined && (
              <p role="status" className="text-xs text-[var(--success)]">
                已加入生词本（标记为「{joinedLabel}」）
              </p>
            )}
          </>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" size="sm" disabled>
              登录后可加入生词本
            </Button>
            <Link to={ROUTES.login} className="text-xs text-primary underline">
              去登录
            </Link>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => speak(target.sentenceText)}
            disabled={speechUnavailable}
            title={speechUnavailable ? '当前浏览器不支持朗读' : '朗读整句'}
          >
            <Volume2 aria-hidden className="size-3.5" />
            朗读整句
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => void copySentence()}>
            <Copy aria-hidden className="size-3.5" />
            {copied ? '已复制' : '复制句子'}
          </Button>
        </div>

        {notice && (
          <p role="status" className={cn('text-xs', notice.startsWith('已') ? 'text-muted-foreground' : 'text-destructive')}>
            {notice}
          </p>
        )}
      </section>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h4 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{children}</h4>;
}
